import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  collaboratorService,
  UpdateCollaboratorRequest,
  ECollaboratorRole,
  ECollaboratorStatus,
  Collaborator,
} from "@/services/collaborator";

interface UpdateCollaboratorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collaborator: Collaborator;
}

export function UpdateCollaboratorModal({
  open,
  onOpenChange,
  collaborator,
}: UpdateCollaboratorModalProps) {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<UpdateCollaboratorRequest>({
    email: collaborator.email,
    password: "",
    firstName: collaborator.firstName,
    lastName: collaborator.lastName,
    role: collaborator.role,
    status: collaborator.status,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        email: collaborator.email,
        password: "",
        firstName: collaborator.firstName,
        lastName: collaborator.lastName,
        role: collaborator.role,
        status: collaborator.status,
      });
    }
  }, [open, collaborator]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCollaboratorRequest) =>
      collaboratorService.update(collaborator.id, data),
    onSuccess: () => {
      toast.success("Colaborador actualizado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["collaborators"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = () => {
    if (
      !formData.email?.trim() ||
      !formData.firstName?.trim() ||
      !formData.lastName?.trim()
    ) {
      toast.error("Email, nombre y apellido son requeridos");
      return;
    }
    updateMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Actualizar colaborador</DialogTitle>
          <DialogDescription>
            Usuario: {collaborator.userName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="colaborador@example.com"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Nueva contraseña</Label>
            <Input
              id="password"
              type="password"
              placeholder="Dejar en blanco para no cambiar"
              value={formData.password}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, password: e.target.value }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Completa solo si deseas cambiar la contraseña
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="firstName">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="firstName"
              placeholder="Juan"
              value={formData.firstName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, firstName: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">
              Apellido <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lastName"
              placeholder="Pérez"
              value={formData.lastName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>Rol</Label>
            <Select
              value={formData.role}
              onValueChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  role: val as ECollaboratorRole,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ECollaboratorRole.OWNER}>
                  Propietario
                </SelectItem>
                <SelectItem value={ECollaboratorRole.SELLER}>
                  Vendedor
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Estado</Label>
            <Select
              value={formData.status}
              onValueChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  status: val as ECollaboratorStatus,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ECollaboratorStatus.ACTIVE}>
                  Activo
                </SelectItem>
                <SelectItem value={ECollaboratorStatus.INACTIVE}>
                  Inactivo
                </SelectItem>
                <SelectItem value={ECollaboratorStatus.SUSPENDED}>
                  Suspendido
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={updateMutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
